import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';

export interface ValuationItem {
    symbole: string;
    nom_societe: string;
    secteur: string | null;
    pays?: string | null;
    cours_actuel: number | null;
    // All models (for counting)
    yield_reversion: number | null;
    gordon_shapiro: number | null;
    chowder: number | null;
    capm: number | null;
    graham: number | null;
    pe: number | null;
    ev_ebitda: number | null;
    ev_ebit: number | null;
    ps: number | null;
    ev_revenue: number | null;
    pb: number | null;
    rim?: number | null;
    epv: number | null;
    graham_number: number | null;
    dcf: number | null;
    /** Score PEG (Lynch) — informatif, hors synthèse IVK pondérée. */
    peg_score: number | null;
    // Classification Peter Lynch
    lynch_categorie?: string | null;
    lynch_titre?: string | null;
    lynch_emoji?: string | null;
    // Range and synthesis
    prix_min_1an: number | null;
    prix_max_1an: number | null;
    prix_moyen: number | null;
    prix_pondere: number | null;
    potentiel_pct: number | null;
    recommendation: string;
    nb_modeles: number;
    fiabilite: string;
    ttm_reliability: number;
    ttm_method: string;
    cache_date?: string | null;
}

export function useValuationRanking(selectedDate?: string) {
    const { token } = useAuth();
    const [data, setData] = useState<ValuationItem[]>([]);
    const [availableDates, setAvailableDates] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const activeToken = token || (typeof window !== 'undefined' ? (localStorage.getItem('token') || localStorage.getItem('access_token')) : null);

    const fetchAvailableDates = useCallback(async () => {
        try {
            const headers: Record<string, string> = {};
            if (activeToken) {
                headers['Authorization'] = `Bearer ${activeToken}`;
            }
            const response = await fetch(`${API_URL}/v1/valuation/dates`, { headers });
            if (response.ok) {
                const dates = await response.json();
                if (Array.isArray(dates)) {
                    setAvailableDates(dates);
                }
            } else {
                console.warn('Valuation dates endpoint returned status:', response.status);
            }
        } catch (err) {
            console.error('Error fetching valuation dates:', err);
        }
    }, [activeToken]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const url = selectedDate && selectedDate !== 'latest'
                ? `${API_URL}/v1/valuation/ranking/all?limit=100&date_val=${selectedDate}`
                : `${API_URL}/v1/valuation/ranking/all?limit=100`;

            const headers: Record<string, string> = {};
            if (activeToken) {
                headers['Authorization'] = `Bearer ${activeToken}`;
            }

            const response = await fetch(url, { headers });
            if (response.ok) {
                const result = await response.json();
                setData(result);
            } else {
                setError('Erreur lors du chargement des données de valorisation');
            }
        } catch (err) {
            setError('Erreur réseau lors du chargement des valorisations');
        } finally {
            setLoading(false);
        }
    }, [activeToken, selectedDate]);

    useEffect(() => {
        fetchAvailableDates();
    }, [fetchAvailableDates]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        availableDates,
        loading,
        error,
        refetch: fetchData
    };
}

/** Réponse de GET /v1/valuation/{symbole}/complete (valuation.py::calculate_complete_valuation). */
export interface CompleteValuation {
    symbole?: string;
    cours_actuel?: number;
    synthesis?: {
        prix_moyen?: number;
        prix_pondere?: number;
        potentiel_pct?: number;
        recommendation?: string;
        fiabilite?: string;
        nb_modeles_valides?: number;
        /** Part de chaque catégorie dans le prix pondéré (en %), renormalisée sur les catégories calculées. */
        methodology?: {
            dividendes_pct?: number;
            multiples_pct?: number;
            intrinseque_pct?: number;
            theorique_pct?: number;
            nb_modeles_par_categorie?: Record<string, number>;
        } | null;
    };
    yield_reversion?: { prix_juste?: number };
    gordon_shapiro?: { prix_juste?: number };
    chowder_projection?: { prix_cible?: number };
    capm_projection?: { prix_cible?: number };
    graham_simplified?: { prix_juste?: number };
    market_multiples?: {
        pe_model?: { prix_juste?: number };
        ev_ebitda_model?: { prix_juste?: number };
        ev_ebit_model?: { prix_juste?: number };
        ps_model?: { prix_juste?: number };
        ev_revenue_model?: { prix_juste?: number };
        pb_model?: { prix_juste?: number };
        rim_model?: { prix_juste?: number };
        epv_model?: { prix_juste?: number };
        graham_number?: { prix_juste?: number };
    };
    dcf_model?: { prix_juste?: number };
}

/** Détail des modèles d'une valeur, à la date choisie (« latest » = dernier calcul). */
export function useCompleteValuation(symbole: string, selectedDate?: string) {
    const { token } = useAuth();
    const [detail, setDetail] = useState<CompleteValuation | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!token) return;
        let annule = false;
        setLoading(true);
        setError(null);
        const url = selectedDate && selectedDate !== 'latest'
            ? `${API_URL}/v1/valuation/${symbole}/complete?date_val=${selectedDate}`
            : `${API_URL}/v1/valuation/${symbole}/complete`;
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
            .then((d: CompleteValuation) => { if (!annule) setDetail(d); })
            .catch(() => { if (!annule) setError('Impossible de charger le détail des modèles.'); })
            .finally(() => { if (!annule) setLoading(false); });
        return () => { annule = true; };
    }, [symbole, selectedDate, token]);

    return { detail, loading, error };
}
